import cn from 'classnames';
import { useEffect, useMemo, useState } from 'react';

import { Criteria } from '@webapp/account/src/components/share/respondents/criteria';
import {
    CAMPAIGN_DEFAULT_QUOTA_NAME,
    DEFAULT_COUNTRY,
    DEFAULT_QUOTA_COUNT,
} from '@webapp/account/src/components/share/respondents/helpers';

import { Add } from '@webapp/common/assets/icons';
import { Button } from '@webapp/common/components/button';
import { Textfield, TextfieldType } from '@webapp/common/components/textfield';
import { API_URL } from '@webapp/common/conf';

import { CriteriaModal } from '@webapp/landing/src/components/ru/respondent-panel/сriteria-modal/criteria-modal';

import css from './calculator.css';

export const Calculator: FC = () => {
    const [numberOfQuestions, setNumberOfQuestions] = useState<number>(10);
    const [numberOfAnswers, setNumberOfAnswers] = useState<number>(DEFAULT_QUOTA_COUNT);
    const [selectedCriteria, setSelectedCriteria] = useState([]);
    const [points, setPoints] = useState([]);
    const [price, setPrice] = useState<number>();
    const [showCriteriaPopup, setShowCriteriaPopup] = useState<boolean>(false);
    const [editCriteriaCode, setEditCriteriaCode] = useState<string | null>(null);
    const [isShowValidateMessage, setIsShowValidateMessage] = useState<boolean>(false);

    const getCategories = async () => {
        const resp = await fetch(`${API_URL}/core/v1/get/data_points`, {
            headers: {
                'Content-Type': 'application/json',
                accept: 'application/json',
            },
        });
        const { dataPoints } = await resp.json();

        setPoints(dataPoints);
    };

    const getPrice = async () => {
        const findPointCountry = points.find((item) => item.code === CAMPAIGN_DEFAULT_QUOTA_NAME);
        const findDefaultCountryOption = findPointCountry?.options.find((item) => item.title === DEFAULT_COUNTRY);
        const quotas = [
            {
                point: CAMPAIGN_DEFAULT_QUOTA_NAME,
                conditions: [{ code: findDefaultCountryOption?.code, count: numberOfAnswers }],
            },
            ...selectedCriteria,
        ];

        const resp = await fetch(
            `${API_URL}/core/v1/get/campaign_pre_cost?totalQuota=${numberOfAnswers}&planLoi=${numberOfQuestions}&quotas=${JSON.stringify(
                quotas,
            )}`,
            {
                headers: {
                    'Content-Type': 'application/json',
                    accept: 'application/json',
                },
            },
        );
        const { cost } = await resp.json();

        setPrice(cost);
    };

    const dataMapper = useMemo(() => {
        if (!points?.length) return null;

        return points.reduce((acc, item) => {
            const newItem = { ...item, optionsMapped: {} };

            item.options.forEach((option) => {
                newItem.optionsMapped[option.code] = option.title;
            });

            acc[item.code] = newItem;

            return acc;
        }, {});
    }, [points]);

    useEffect(() => {
        getCategories();
    }, []);

    if (!points?.length) return null;

    return (
        <div className={css.campaign}>
            <div className={css.campaignWrap}>
                <div className={css.campaignFields}>
                    <div>
                        <div>Количество вопросов</div>
                        <Textfield
                            type={TextfieldType.NUMBER}
                            value={numberOfQuestions}
                            onChange={(value) => {
                                if (value > 70) {
                                    setNumberOfQuestions(70);
                                    setIsShowValidateMessage(true);
                                } else {
                                    setNumberOfQuestions(value);
                                    setIsShowValidateMessage(false);
                                }
                                setPrice(null);
                            }}
                            max={70}
                        />
                        {isShowValidateMessage && <div className={css.message}>Значение не может превышать 70</div>}
                    </div>
                    <div>
                        <div>Количество ответов</div>
                        <Textfield
                            type={TextfieldType.NUMBER}
                            value={numberOfAnswers}
                            onChange={(value) => {
                                setNumberOfAnswers(value);
                                setPrice(null);
                            }}
                        />
                    </div>
                </div>
            </div>
            {selectedCriteria.length > 0 && (
                <div className={css.criteriaList}>
                    {selectedCriteria.map((item) => {
                        if (item.point === CAMPAIGN_DEFAULT_QUOTA_NAME) return null;

                        return (
                            <Criteria
                                key={item.point}
                                data={item}
                                point={dataMapper[item.point]}
                                quota={numberOfAnswers}
                                isEditDisabled={false}
                                onChange={(data) => {
                                    setSelectedCriteria((prev) => {
                                        return prev.map((item) => (item.point === data.point ? data : item));
                                    });
                                    setPrice(null);
                                }}
                                onRemove={(code) => {
                                    setSelectedCriteria((prev) => {
                                        return prev.filter((item) => item.point !== code);
                                    });
                                    setPrice(null);
                                }}
                                onAddCondition={(code: string) => {
                                    setEditCriteriaCode(code);
                                    setShowCriteriaPopup(true);
                                }}
                            />
                        );
                    })}
                </div>
            )}
            <div className={cn(css.addCriteria)} onClick={() => setShowCriteriaPopup(true)}>
                <Add className={css.addIcon} />
                Добавить критерии
            </div>
            <Button
                disabled={!numberOfQuestions || !numberOfAnswers || selectedCriteria.length === 0}
                color='blue'
                onClick={getPrice}
                className={css.campaignButton}
                responsive={true}
            >
                Рассчитать
            </Button>
            <div className={css.collectResponses}>
                <div>Количество ответов {numberOfAnswers}</div>
                <div className={css.collectResponsesGroup}>
                    <div className={css.info}>
                        Стоимость <span className={css.infoBolder}>{price ? `${price} руб.` : '-'}</span>
                    </div>
                </div>
            </div>
            {showCriteriaPopup && (
                <CriteriaModal
                    points={points}
                    editCriteriaCode={editCriteriaCode}
                    savedCriteriaList={selectedCriteria}
                    quota={numberOfAnswers}
                    onSave={(data, isEdit) => {
                        setSelectedCriteria((prev) => {
                            return isEdit
                                ? prev.map((item) => (item.point === data.point ? data : item))
                                : [...prev, data];
                        });
                        setPrice(null);
                    }}
                    onClose={() => {
                        setShowCriteriaPopup(false);
                        setEditCriteriaCode(null);
                    }}
                />
            )}
        </div>
    );
};
